import { Controller } from "@hotwired/stimulus";
import { modalLinkUrl, setModalLinkUrl } from "../../utils/modal_link";

export default class extends Controller {
  static targets = ["emptyState", "list", "itemTemplate", "sendButton", "labelsModal"];
  static values = {
    valuers: Array, // [{id, title, subtitle?, badges?: [...] }]
    disableSendButton: Boolean,
  };

  connect() {
    this.items = Array.isArray(this.valuersValue) ? [...this.valuersValue] : [];
    this.currentValuerId = null;
    this.renderAll();

    this._onSet  = (e) => this.setValuers(e.detail || []);
    this._onAdd  = (e) => this.addValuer(e.detail);
    this._onDrop = (e) => this.removeById(e.detail?.id);
    this._onLabelsSelected = (e) => this.handleLabelsSelected(e.detail);
    this._onContactsSelected = (e) => this.handleContactsSelected(e.detail);

    window.addEventListener("valuersSelection:set", this._onSet);
    window.addEventListener("valuersSelection:add", this._onAdd);
    window.addEventListener("valuersSelection:remove", this._onDrop);
    window.addEventListener("quoteLabels:selected", this._onLabelsSelected);
    window.addEventListener("quoteContacts:selected", this._onContactsSelected);
  }

  disconnect() {
    window.removeEventListener("valuersSelection:set", this._onSet);
    window.removeEventListener("valuersSelection:add", this._onAdd);
    window.removeEventListener("valuersSelection:remove", this._onDrop);
    window.removeEventListener("quoteLabels:selected", this._onLabelsSelected);
    window.removeEventListener("quoteContacts:selected", this._onContactsSelected);
  }

  // ===== Public API =====
  setValuers(newItems) {
    this.items = (newItems || []).filter(Boolean);
    this.renderAll();
  }

  addValuer(item) {
    if (!item || item.id == null) return;
    const id = String(item.id);
    if (!this.items.some(v => String(v.id) === id)) {
      this.items.push(item);
      if (item.contactsUrl) {
        this._fetchContactsAvailability(item);
      } else {
        this.renderAll();
      }
    }
  }

  removeById(id) {
    if (id == null) return;
    const sid = String(id);
    const before = this.items.length;
    this.items = this.items.filter(v => String(v.id) !== sid);
    if (this.items.length !== before) {
      this.renderAll();
      this._emit("valuersSelection:remove", { id });
    }
  }

  // ===== Actions =====
  remove(event) {
    const node = event.currentTarget.closest("[data-item]");
    if (!node) return;
    const id = node.dataset.id;
    this.removeById(id);
  }

  openLabelsModal(event) {
    this._openModal(event, this.labelsModalTarget, (valuer) =>
      valuer?.labels?.map(l => l.value) || []
    );
  }

  openContactsModal(event) {
    const node = event.currentTarget.closest("[data-item]");
    if (node) {
      this.currentValuerId = node.dataset.id;

      const link = event.currentTarget;
      const url = new URL(modalLinkUrl(link), window.location.origin);
      url.searchParams.set('valuation_firm_id', this.currentValuerId);
      setModalLinkUrl(link, url.toString());

      const handleSuccess = () => {
        this._preselectContactsInModal();
        link.removeEventListener('ajax:success', handleSuccess);
      };
      link.addEventListener('ajax:success', handleSuccess);
    }
  }

  _preselectContactsInModal() {
    const valuer = this.items.find(v => String(v.id) === String(this.currentValuerId));
    if (!valuer) return;

    const savedContactIds = valuer?.contacts?.map(c => String(c.id)) || [];
    if (savedContactIds.length === 0) return;

    this._waitForModal('add-quote-contacts-modal', 50, 20)
      .then((modal) => {
        const multiSelectControllerEl = modal.querySelector('[data-controller="multi-select-chips-with-search"]');
        if (!multiSelectControllerEl) return;

        // Dispatch a custom event to the multi-select controller to preselect contacts
        const event = new CustomEvent('multi-select-chips-with-search:preselect', {
          detail: { selectedIds: savedContactIds },
          bubbles: true,
        });
        multiSelectControllerEl.dispatchEvent(event);
      })
      .catch(() => {
        console.warn('Failed to preselect contacts: modal did not render in time');
      });
  }

  _waitForModal(modalId, intervalMs = 50, maxAttempts = 20) {
    return new Promise((resolve, reject) => {
      let attempts = 0;

      const checkModal = () => {
        const modal = document.getElementById(modalId);
        if (modal) {
          resolve(modal);
        } else if (attempts >= maxAttempts) {
          reject(new Error(`Modal with ID "${modalId}" not found after ${maxAttempts} attempts`));
        } else {
          attempts++;
          setTimeout(checkModal, intervalMs);
        }
      };

      checkModal();
    });
  }

  handleContactsSelected(detail) {
    if (!this.currentValuerId || !detail?.contacts) return;

    const valuer = this.items.find(v => String(v.id) === String(this.currentValuerId));
    if (valuer) {
      valuer.contacts = detail.contacts;
      this.renderAll();
      this._updateValuerCardContacts(this.currentValuerId, detail.contacts);
    }

    this.currentValuerId = null;
  }

  _openModal(event, modalTarget, getSelectedValues) {
    event.stopPropagation();
    event.preventDefault();

    const node = event.currentTarget.closest("[data-item]");
    if (!node) return;

    this.currentValuerId = node.dataset.id;

    // Get current valuer's saved values
    const valuer = this.items.find(v => String(v.id) === String(this.currentValuerId));
    const savedValues = getSelectedValues(valuer);

    const modal = modalTarget.querySelector('.modal');
    if (modal) {
      // Update multi-select to show current valuer's selections
      const multiSelectController = modal.querySelector('[data-controller="multi-select-chips-with-search"]');
      if (multiSelectController) {
        const stimulus = this.application.getControllerForElementAndIdentifier(
          multiSelectController,
          'multi-select-chips-with-search'
        );
        if (stimulus) {
          // Clear all first
          stimulus.clearSelection();

          // Then set this valuer's saved values
          const inputs = stimulus.dropdownEl.querySelectorAll('input[type="checkbox"], input[type="radio"]');
          inputs.forEach((input) => {
            input.checked = savedValues.includes(String(input.value));
          });
          stimulus._renderChipsFromDOM();
        }
      }

      modal.classList.remove('hidden');
      modal.classList.add('flex');
    }
  }

  handleLabelsSelected(detail) {
    if (!this.currentValuerId || !detail?.labels) return;

    const valuer = this.items.find(v => String(v.id) === String(this.currentValuerId));
    if (valuer) {
      valuer.labels = detail.labels;
      this.renderAll();
      this._updateValuerCardLabels(this.currentValuerId, detail.labels);
    }

    this.currentValuerId = null;
  }

  handleContactsSelected(detail) {
    if (!this.currentValuerId || !detail?.contacts) return;

    const valuer = this.items.find(v => String(v.id) === String(this.currentValuerId));
    if (valuer) {
      valuer.contacts = detail.contacts;
      this.renderAll();
      this._updateValuerCardContacts(this.currentValuerId, detail.contacts);
    }

    this.currentValuerId = null;
  }

  // ===== Render =====
  renderAll() {
    const hasItems = this.items.length > 0;
    this.emptyStateTarget.classList.toggle("hidden", hasItems);
    this.listTarget.innerHTML = "";

    this.items.forEach((item, index) => {
      const node = this.itemTemplateTarget.content.firstElementChild.cloneNode(true);
      node.dataset.id = item.id;

      // title
      const titleEl = node.querySelector("[data-field='title']");
      if (titleEl) titleEl.textContent = item.title || `Valuer #${item.id}`;

      // hidden inputs (backend)
      const idInput = node.querySelector("[data-field='id']");
      if (idInput) idInput.value = item.id;

      // show/hide contact section based on availability
      const contactSection = node.querySelector("[data-field='contactSection']");
      if (contactSection) {
        if (item.hasContacts === false) {
          contactSection.style.display = 'none';
        } else {
          contactSection.style.display = '';
        }
      }

      const contactIdsInput = node.querySelector("[data-field='quoteContacts']");
      if (contactIdsInput && Array.isArray(item.contacts)) {
        contactIdsInput.value = JSON.stringify(item.contacts.map(c => c.id));
      }

      const contactsContainer = node.querySelector("[data-field='contactList']");
      if (contactsContainer && Array.isArray(item.contacts) && item.contacts.length > 0) {
        contactsContainer.innerHTML = "";
        item.contacts.forEach((contact) => {
          const chip = document.createElement("span");
          chip.className = "border border-black-100 pl-4 py-4 pr-6 bg-transparent rounded-[20px] flex items-center gap-6 text-black-1000 text-paragraph-s";

          const avatarDiv = document.createElement("div");
          avatarDiv.className = "w-20 h-20 rounded-full bg-black-50 border border-black-400 flex items-center justify-center text-black-400 font-medium shrink-0";

          const initials = contact.name?.split(' ').map(w => w[0]?.toUpperCase()).join('').slice(0, 2) || '?';
          const initialsSpan = document.createElement("span");
          initialsSpan.className = "text-[11px]";
          initialsSpan.textContent = initials;
          avatarDiv.appendChild(initialsSpan);

          chip.appendChild(avatarDiv);

          const nameSpan = document.createElement("span");
          nameSpan.textContent = contact.name || 'Unknown';
          chip.appendChild(nameSpan);

          contactsContainer.appendChild(chip);
        });
      }

      const labelIdsInput = node.querySelector("[data-field='labelIds']");
      if (labelIdsInput && Array.isArray(item.labels)) {
        labelIdsInput.value = JSON.stringify(item.labels.map(l => l.value));
      }

      const labelsContainer = node.querySelector("[data-field='labelChips']");
      if (labelsContainer && Array.isArray(item.labels) && item.labels.length > 0) {
        labelsContainer.innerHTML = "";
        item.labels.forEach((label) => {
          const chip = document.createElement("div");
          chip.className = "box-border max-w-fit text-center py-6 px-8 rounded-8 border";
          chip.dataset.controller = "label-chip";
          chip.dataset.bgColor = label.color;
          chip.dataset.textColor = label.text_color;
          chip.style.backgroundColor = label.color;
          chip.style.borderColor = label.color;
          chip.style.color = label.text_color;

          const span = document.createElement("span");
          span.className = "block text-paragraph-s font-medium text-nowrap capitalize";
          span.textContent = label.name;

          chip.appendChild(span);
          labelsContainer.appendChild(chip);
        });
      }

      const badgesWrap = node.querySelector("[data-field='badges']");
      if (badgesWrap) {
        const customBadgeLabel = badgesWrap.querySelector("[data-field='badge-label']");

        if (item.badgeLabel && customBadgeLabel) {
          customBadgeLabel.classList.remove('hidden');
          const labelText = customBadgeLabel.querySelector('span');
          if (labelText) {
            labelText.textContent = item.badgeLabel;
          }

          Array.from(badgesWrap.children).forEach(child => {
            if (child.dataset.field !== 'badge-label') {
              child.remove();
            }
          });
        } else {
          if (customBadgeLabel) {
            customBadgeLabel.classList.add('hidden');
          }

          if (Array.isArray(item.badges)) {
            Array.from(badgesWrap.children).forEach(child => {
              if (child.dataset.field !== 'badge-label') {
                child.remove();
              }
            });

            item.badges.forEach((badge) => {
              const badgeElement = this._renderBadge(badge, item.amount, node);
              if (badgeElement) {
                badgesWrap.appendChild(badgeElement);
              }
            });
          }
        }
      }

      // --- divider only from second item ---
      if (index === 0) {
        node.classList.remove("border-t", "border-black-100");
      } else {
        node.classList.add("border-t", "border-black-100");
      }

      this.listTarget.appendChild(node);
    });

    this._setSendDisabled(!hasItems);
  }

  _setSendDisabled(disabled) {
    const btn = this.sendButtonTarget;
    if (!btn) return;
    if (disabled || this.disableSendButtonValue) {
      btn.setAttribute("disabled", "disabled");
      btn.classList.add("opacity-60", "pointer-events-none");
    } else {
      btn.removeAttribute("disabled");
      btn.classList.remove("opacity-60", "pointer-events-none");
    }
  }

  // ===== Utils =====
  _emit(name, detail) {
    window.dispatchEvent(new CustomEvent(name, { detail }));
  }

  async _fetchContactsAvailability(item) {
    try {
      const response = await fetch(item.contactsUrl, {
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      });

      if (response.ok) {
        const data = await response.json();
        const valuer = this.items.find(v => String(v.id) === String(item.id));
        if (valuer) {
          valuer.hasContacts = data.contacts && data.contacts.length > 0;
        }
      }
    } catch (error) {
      console.error('Failed to fetch contacts:', error);
    } finally {
      this.renderAll();
    }
  }

  _updateValuerCardContacts(valuerId, contacts) {
    const card = document.querySelector(`[data-card][data-id="${CSS.escape(String(valuerId))}"]`);
    if (!card) return;

    const container = card.querySelector('[data-field="contact-ids-container"]');
    if (!container) return;

    container.innerHTML = '';

    if (Array.isArray(contacts) && contacts.length > 0) {
      const baseName = this._extractBaseName(card, 'valuation_firm_id');
      if (!baseName) return;

      contacts.forEach(contact => {
        if (contact.id != null) {
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = baseName.replace('[valuation_firm_id]', '[contact_ids][]');
          input.value = contact.id;
          input.disabled = !this._isCardSelected(card);
          container.appendChild(input);
        }
      });
    }
  }

  _updateValuerCardLabels(valuerId, labels) {
    const card = document.querySelector(`[data-card][data-id="${CSS.escape(String(valuerId))}"]`);
    if (!card) return;

    const container = card.querySelector('[data-field="label-list-container"]');
    if (!container) return;

    container.innerHTML = '';

    if (Array.isArray(labels) && labels.length > 0) {
      const baseName = this._extractBaseName(card, 'valuation_firm_id');
      if (!baseName) return;

      labels.forEach(label => {
        const labelValue = label.value || label.name;
        if (labelValue != null) {
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = baseName.replace('[valuation_firm_id]', '[label_list][]');
          input.value = labelValue;
          input.disabled = !this._isCardSelected(card);
          container.appendChild(input);
        }
      });
    }
  }

  _extractBaseName(card, fieldSuffix) {
    const existingInput = card.querySelector(`input[name*="[${fieldSuffix}]"]`);
    return existingInput ? existingInput.name : null;
  }

  _isCardSelected(card) {
    const valuationFirmIdInput = card.querySelector('input[name*="[valuation_firm_id]"]');
    return valuationFirmIdInput && !valuationFirmIdInput.disabled;
  }

  _renderBadge(badgeType, amount, node) {
    const template = node.querySelector(`template[data-badge-template="${badgeType}"]`);
    if (!template) {
      const fallbackSpan = document.createElement('span');
      fallbackSpan.className = 'px-8 py-4 rounded-8 bg-black-50 border border-black-100 text-paragraph-s';
      fallbackSpan.textContent = badgeType;
      return fallbackSpan;
    }

    const badgeElement = template.content.firstElementChild.cloneNode(true);

    const amountDisplay = badgeElement.querySelector('[data-amount-display]');
    if (amountDisplay && amount && parseFloat(amount) > 0) {
      const formattedAmount = parseFloat(amount).toLocaleString('en-GB', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
      amountDisplay.textContent = `£${formattedAmount}`;

    } else if (amountDisplay) {
      amountDisplay.remove();
    }

    return badgeElement;
  }
}
